import { DateTime } from 'luxon'

// Position after 'YYYY-MM-DD' to check for timezone offset hyphens (e.g., -05:00)
const ISO_DATE_PORTION_STRING_LENGTH = 10

// ============================================================================
// PRIVATE HELPER FUNCTIONS
// ============================================================================

/**
 * @param dateTimeString
 * @returns date portion of string if valid, otherwise null. This is used to defensively parse date strings that may include time or timezone info, for fields that should be date-only (e.g., DOB).
 * @example
 * _removeTimeFromDateTimeString('2026-01-16T20:30:00')  // '2026-01-16'
 * _removeTimeFromDateTimeString('2026-01-16 20:30:00')  // '2026-01-16'
 * _removeTimeFromDateTimeString('2026-01-16')           // '2026-01-16'
 * _removeTimeFromDateTimeString('2026-01-16-05:00')    // null (invalid - time portion should be separated by T or space)
 */
function _removeTimeFromDateTimeString(dateTimeString: string): string | null {
  let datePart = dateTimeString
  // If the string includes a 'T', split and take the date part
  if (datePart.includes('T')) {
    datePart = datePart.split('T')[0]
  }
  // If it includes a space, split and take the date part
  if (datePart.includes(' ')) {
    datePart = datePart.split(' ')[0]
  }
  // Otherwise, check string to ensure it doesn't have a time portion (e.g., '2026-01-16-05:00' would be invalid)
  if (
    datePart.length === ISO_DATE_PORTION_STRING_LENGTH &&
    /^\d{4}-\d{2}-\d{2}$/.test(datePart)
  ) {
    return datePart
  }
  console.error(
    `Invalid date string "${dateTimeString}" - expected date portion in YYYY-MM-DD format.`
  )
  return null
}

/**
 * Normalizes a UTC datetime string by ensuring it has a 'T' separator and a 'Z' suffix if no timezone is present.
 * @param utcString - The UTC datetime string to normalize
 * @returns The normalized UTC datetime string
 */
function _normalizeUtcStringTimezone(utcString: string): string {
  // +00:00 and -00:00 are both valid ISO 8601 expressions of UTC; treat them
  // as UTC-equivalent so we don't warn and don't append a redundant 'Z'.
  const isUtcZeroOffset =
    utcString.includes('+00:00') ||
    utcString.includes('-00:00', ISO_DATE_PORTION_STRING_LENGTH)

  const hasNonUTCTimezoneIndicator =
    !isUtcZeroOffset &&
    (utcString.includes('+') ||
      utcString.includes('-', ISO_DATE_PORTION_STRING_LENGTH))

  if (hasNonUTCTimezoneIndicator) {
    console.warn(
      `Warning: Expected UTC string "${utcString}" includes a timezone offset. This may lead to incorrect parsing.`
    )
  }
  const hasAnyTimezoneIndicator =
    utcString.includes('Z') || isUtcZeroOffset || hasNonUTCTimezoneIndicator

  const normalizedTimeIndicatorUtcString = utcString.replace(' ', 'T')
  if (hasAnyTimezoneIndicator) {
    return normalizedTimeIndicatorUtcString
  }
  // Date-only strings (no 'T') need a time component before the 'Z' to be valid ISO
  return normalizedTimeIndicatorUtcString.includes('T')
    ? `${normalizedTimeIndicatorUtcString}Z`
    : `${normalizedTimeIndicatorUtcString}T00:00:00Z`
}

/**
 * Converts a Luxon DateTime to a JS Date whose wall-clock time matches the
 * target timezone — needed because PrimeVue pickers always display in local time.
 *
 * Inverse of {@link _shiftLocalDateFromPrimevueToLxDateTime}.
 * @param lxDateTime - Luxon DateTime (typically in UTC from the API)
 * @param destTimezone - IANA timezone to display in (e.g., 'America/New_York').
 *   If omitted, uses the user's local timezone.
 * @returns JS Date with wall-clock time matching destTimezone, or null if invalid
 * @example
 * // API value: '2026-01-16T20:30:00Z' (8:30 PM UTC)
 * // destTimezone: 'America/New_York' (UTC-5)
 * _shiftDatetimeToLocalDateForPrimevue(lxDateTime, 'America/New_York')
 * // → Date whose getHours() === 15 (3:30 PM), so PrimeVue shows 3:30 PM
 */
function _shiftDatetimeToLocalDateForPrimevue(
  lxDateTime: DateTime,
  destTimezone?: string | null
): Date {
  if (destTimezone) {
    return lxDateTime
      .setZone(destTimezone)
      .setZone('local', { keepLocalTime: true })
      .toJSDate()
  }

  return lxDateTime.setZone('local').toJSDate()
}

/**
 * Converts a local JS Date from a PrimeVue picker back to a Luxon DateTime in
 * UTC, interpreting the wall-clock time as being in the source timezone.
 *
 * Inverse of {@link _shiftDatetimeToLocalDateForPrimevue}.
 * @param localDate - JS Date from a PrimeVue component
 * @param sourceTimezone - IANA timezone the date was displayed in (e.g., 'America/New_York').
 *   If omitted, uses the user's local timezone.
 * @returns Luxon DateTime in UTC
 * @example
 * // PrimeVue shows 3:30 PM for 'America/New_York' (UTC-5)
 * // localDate.getHours() === 15
 * _shiftLocalDateFromPrimevueToLxDateTime(localDate, 'America/New_York')
 * // → Luxon DateTime at 2026-01-16T20:30:00Z (8:30 PM UTC)
 */
function _shiftLocalDateFromPrimevueToLxDateTime(
  localDate: Date,
  sourceTimezone?: string | null
): DateTime {
  const localDt = DateTime.fromJSDate(localDate)

  if (sourceTimezone) {
    return localDt.setZone(sourceTimezone, { keepLocalTime: true }).toUTC()
  }

  return localDt.toUTC()
}

/**
 * Creates or updates a Date with a new date portion, preserving or setting time.
 * Use when the date picker changes.
 * @param existingDatetime - Optional existing datetime to preserve time from
 * @param dateOverride - Date to get date portion from (year, month, day)
 * @returns Date with date from dateOverride and time from existingDatetime (or midnight)
 * @example
 * // User picks new date, preserve existing time
 * const existing = new Date(2026, 0, 16, 15, 30) // Jan 16, 3:30 PM
 * const newDate = new Date(2026, 1, 20)           // Feb 20
 * _withDateOverride(existing, newDate)
 * // Returns: Feb 20, 2026 3:30 PM
 */
function _withDateOverride(existingDatetime: Date, dateOverride: Date): Date {
  const dateDt = DateTime.fromJSDate(dateOverride)
  const existingDt = DateTime.fromJSDate(existingDatetime)

  return existingDt
    .set({
      year: dateDt.year,
      month: dateDt.month,
      day: dateDt.day,
    })
    .toJSDate()
}

/**
 * Creates or updates a Date with a new time portion, preserving date.
 * Use when the time picker changes, or to merge separate date/time pickers.
 * @param existingDatetime - Date to get date portion from (year, month, day)
 * @param timeOverride - Date to get time portion from (hour, minute, second, millisecond)
 * @returns Date with date from existingDatetime and time from timeOverride
 * @example
 * // Update existing datetime with new time
 * const existing = new Date(2026, 0, 16, 10, 0)        // Jan 16, 10:00:00.000 AM
 * const newTime = new Date(2026, 5, 1, 17, 45, 30, 0)  // 5:45:30 PM
 * _withTimeOverride(existing, newTime)
 * // Returns: Jan 16, 2026 5:45:30.000 PM
 */
function _withTimeOverride(existingDatetime: Date, timeOverride: Date): Date {
  const baseDt = DateTime.fromJSDate(existingDatetime)
  const timeDt = DateTime.fromJSDate(timeOverride)

  return baseDt
    .set({
      hour: timeDt.hour,
      minute: timeDt.minute,
      second: timeDt.second,
      millisecond: timeDt.millisecond,
    })
    .toJSDate()
}

/**
 * Returns the default Luxon display format based on whether time should be included.
 * @param includeTime - If true, returns DATETIME_FULL; otherwise DATE_SHORT.
 * @returns Intl.DateTimeFormatOptions for use with toLocaleString()
 */
function _getDefaultDateTimeStringFormat(
  includeTime: boolean
): Intl.DateTimeFormatOptions {
  return includeTime ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT
}

/**
 * Formats a Luxon DateTime for display using the given format or a sensible default.
 * @param lxDateTime - Luxon DateTime to format
 * @param includeTime - If true, uses a date+time format. If false, date only.
 * @param format - Optional Intl.DateTimeFormatOptions override (e.g., DateTime.DATE_SHORT)
 * @returns Formatted string, or 'Invalid date' if the DateTime is invalid
 */
function _formatLxDateTimeForDisplay(
  lxDateTime: DateTime,
  includeTime: boolean,
  format?: Intl.DateTimeFormatOptions
): string {
  if (!lxDateTime.isValid) {
    return 'Invalid date'
  }

  const selectedFormat = format ?? _getDefaultDateTimeStringFormat(includeTime)

  return lxDateTime.toLocaleString(selectedFormat)
}

// ============================================================================
// PUBLIC FUNCTIONS
// ============================================================================

/**
 * Parses a UTC datetime string from the API and returns a Luxon DateTime.
 * This is the foundational parsing function - always assumes API dates are UTC.
 * @param utcString - The datetime string from the database (assumed to be UTC)
 * @param dateOnly - If true, only the date portion is parsed (ignores time)
 * @returns Luxon DateTime in UTC, or null if invalid/empty
 * @example
 * parseUtcStringToLxDateTime('2026-01-16T20:30:00')  // DateTime in UTC
 * parseUtcStringToLxDateTime('2026-01-16 20:30:00')  // Also works (space separator)
 */
export function parseUtcStringToLxDateTime(
  utcString: string | null | undefined,
  dateOnly: boolean = false
): DateTime | null {
  if (!utcString) return null

  if (dateOnly) {
    const dateOnlyString = _removeTimeFromDateTimeString(utcString)
    if (!dateOnlyString) {
      return null
    }
    return DateTime.fromISO(dateOnlyString)
  }

  const normalizedUtcDateTimeString = _normalizeUtcStringTimezone(utcString)
  const dt = DateTime.fromISO(normalizedUtcDateTimeString, { zone: 'utc' })
  return dt.isValid ? dt : null
}

/**
 * Parses a UTC datetime string and formats it for display, optionally including time.
 * @param utcString - UTC datetime string from API
 * @param includeTime - If true, converts to local TZ and includes time. If false, date only.
 * @param format - Optional Luxon format (DATETIME_FULL, DATE_SHORT, etc.)
 * @param destTimezone - Optional IANA timezone (e.g., 'America/Chicago'). Defaults to user's local timezone. Only applied when includeTime is true.
 * @returns Formatted string, or 'Invalid date' if invalid
 * @example
 * utcStringToDisplayString('2026-01-16T20:30:00', true)
 * // User in EST: '1/16/2026, 3:30 PM'
 * @example
 * utcStringToDisplayString('2026-01-16T20:30:00', true, DateTime.DATETIME_SHORT, 'America/Chicago')
 * // '1/16/2026, 2:30 PM'
 * @example
 * utcStringToDisplayString('2026-01-16T20:30:00', false, DateTime.DATE_FULL)
 * // 'January 16, 2026'
 */
export function utcStringToDisplayString(
  utcString: string,
  includeTime: boolean = false,
  format?: Intl.DateTimeFormatOptions,
  destTimezone?: string | null
): string {
  if (destTimezone != null && !includeTime) {
    throw new Error('destTimezone requires includeTime to be true')
  }

  const utcDateTime = parseUtcStringToLxDateTime(utcString, !includeTime)
  if (!utcDateTime) {
    console.error(
      `Failed to parse UTC string "${utcString}" includeTime: ${includeTime} as a DateTime.`
    )
    return 'Invalid date'
  }

  const displayDateTime = includeTime
    ? utcDateTime.setZone(destTimezone ?? 'local')
    : utcDateTime
  return _formatLxDateTimeForDisplay(displayDateTime, includeTime, format)
}

/**
 * Formats the current datetime for display in the user's local timezone.
 * @param includeTimezone - If true, appends the timezone abbreviation
 * @returns Formatted string
 * @example
 * nowToDisplayString()      // '1/16/2026, 10:30 AM'
 * @example
 * nowToDisplayString(true)  // '1/16/2026, 10:30 AM EST'
 */
export function nowToDisplayString(includeTimezone: boolean = false): string {
  const formatted = _formatLxDateTimeForDisplay(DateTime.now(), true)
  return includeTimezone ? `${formatted} ${getTimezoneShort()}` : formatted
}

// ============================================================================
// PRIMEVUE SPECIFIC DATE CONVERSIONS
// ============================================================================

/**
 * Converts a UTC API string to a JS Date.
 * Pass a timezone to display in that timezone (e.g., patient's timezone).
 * Omit timezone to use user's local timezone.
 *
 * For timezone display: The returned Date's wall-clock time (getHours, etc.)
 * will match the target timezone, making it work correctly with PrimeVue components.
 * @param utcString - UTC datetime string from API
 * @param destTimezone - Optional IANA timezone (e.g., 'America/New_York'). Defaults to local.
 * @returns JS Date showing correct wall-clock time, or null if invalid
 * @example
 * // API: '2026-01-16T20:30:00' (8:30 PM UTC), user in PST
 * utcStringToPrimevueComponentDate('2026-01-16T20:30:00')
 * // Returns: Date for 12:30 PM PST (user's local time)
 * @example
 * // Display in patient's EST timezone instead
 * utcStringToPrimevueComponentDate('2026-01-16T20:30:00', 'America/New_York')
 * // Returns: Date where getHours() === 15 (3:30 PM EST)
 */
export function utcStringToPrimevueComponentDate(
  utcString: string,
  destTimezone?: string | null
): Date
export function utcStringToPrimevueComponentDate(
  utcString: string | null | undefined,
  destTimezone?: string | null
): Date | null
export function utcStringToPrimevueComponentDate(
  utcString: string | null | undefined,
  destTimezone?: string | null
): Date | null {
  const dt = parseUtcStringToLxDateTime(utcString)
  if (!dt) return null

  return _shiftDatetimeToLocalDateForPrimevue(dt, destTimezone)
}

/**
 * Converts a JS Date back to a UTC ISO string for API submission.
 * Pass a timezone if the Date was displayed/edited in a specific timezone context.
 * @param date - JS Date from PrimeVue component
 * @param timezone - Timezone the date was displayed in. Omit for local timezone.
 * @param dateOnly - When true, returns only the date portion (YYYY-MM-DD) of the UTC instant.
 * @returns UTC ISO string for API, or null if invalid
 * @example
 * // User selects 3:30 PM in their local EST timezone
 * primevueComponentDateToUtcString(new Date(2026, 0, 16, 15, 30))
 * // Returns: '2026-01-16T20:30:00.000Z'
 * @example
 * // Date was displayed in patient's CST timezone
 * primevueComponentDateToUtcString(date, 'America/Chicago')
 * // Interprets Date's wall time as CST before converting to UTC
 * @example
 * // Date-only field — returns the UTC date with no time component
 * primevueComponentDateToUtcString(new Date(2026, 0, 16), null, true)
 * // Returns: '2026-01-16'
 */
export function primevueComponentDateToUtcString(
  date: Date | null | undefined,
  timezone?: string | null,
  dateOnly: boolean = false
): string | null {
  if (!date) return null
  const lxDateTime = _shiftLocalDateFromPrimevueToLxDateTime(date, timezone)
  if (!lxDateTime.isValid) return null
  const isoString = lxDateTime.toUTC().toISO()
  return dateOnly ? _removeTimeFromDateTimeString(isoString ?? '') : isoString
}

// ============================================================================
// ZONE-LESS DATE PARSING (for sa.Date fields like dueDate)
// ============================================================================

/**
 * Converts a zone-less date string (YYYY-MM-DD) to a JavaScript Date at midnight local time.
 * Use this for fields stored as sa.Date on the backend (e.g., dueDate, dateOfBirth).
 * The returned Date represents midnight on that calendar date in the user's local timezone.
 *
 * IMPORTANT: Use this instead of `new Date(dateString)` which parses as UTC midnight,
 * causing timezone shifts (e.g., '2026-09-10' → 8pm EDT on 9/9).
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns JS Date at midnight local time for that calendar date, or null if invalid
 * @example
 * // Backend sends dueDate: '2026-01-16'
 * plainStringToLocalDate('2026-01-16')
 * // Returns: Date representing Jan 16, 2026 00:00:00 in user's local timezone
 */
export function plainStringToLocalDate(
  dateString: string | null | undefined
): Date | null {
  if (!dateString) return null
  const dt = DateTime.fromISO(dateString, { zone: 'local' })
  return dt.isValid ? dt.toJSDate() : null
}

// ============================================================================
// DATE/TIME PART MANIPULATION (for PrimeVue separate pickers)
// ============================================================================

/**
 * Combines separate date and time overrides into a single Date, using an optional base date for defaults.
 * Useful for merging separate date and time pickers into a single datetime for API submission.
 * @param options - Object containing date/time overrides
 * @param options.dateOverride - Date to get date portion from (year, month, day)
 * @param options.timeOverride - Date to get time portion from (hour, minute, second, millisecond)
 * @param options.baseDate - Existing date to use as a base for any missing parts. Defaults to now.
 * @returns Date with combined date and time
 * @example
 * // User edits date and time separately, then we combine them for API
 * const dateOverride = new Date(2026, 0, 16)       // Jan 16
 * const timeOverride = new Date(2026, 5, 1, 15, 30) // 3:30 PM
 * combineDateTimes({ dateOverride, timeOverride })
 * // Returns: Jan 16, 2026 3:30 PM
 * @example
 * // Only time is overridden, use baseDate for the date portion
 * const existing = new Date(2026, 0, 16, 10, 0)    // Jan 16, 10:00 AM
 * const timeOverride = new Date(2026, 5, 1, 15, 30) // 3:30 PM
 * combineDateTimes({ timeOverride, baseDate: existing })
 * // Returns: Jan 16, 2026 3:30 PM
 */
export function combineDateTimes({
  dateOverride,
  timeOverride,
  baseDate = new Date(),
}: {
  dateOverride?: Date
  timeOverride?: Date
  baseDate?: Date
}): Date {
  let combinedDate = baseDate
  if (dateOverride) {
    combinedDate = _withDateOverride(baseDate, dateOverride)
  }
  if (timeOverride) {
    combinedDate = _withTimeOverride(combinedDate, timeOverride)
  }
  return combinedDate
}

// ============================================================================
// TIMEZONE DISPLAY
// ============================================================================

/**
 * Gets the short timezone abbreviation (e.g., 'EST', 'PST').
 * @param timezone - IANA timezone, or omit for local timezone
 * @param atDatetime - Date to check DST status for, or omit for current time
 */
export function getTimezoneShort(
  timezone?: string | null,
  atDatetime?: Date | null
): string {
  let dt = atDatetime ? DateTime.fromJSDate(atDatetime) : DateTime.now()
  if (timezone) {
    dt = dt.setZone(timezone)
  }
  return dt.offsetNameShort ?? ''
}

/**
 * Format an ISO datetime string to a human-readable time with timezone abbreviation.
 * Uses Luxon's format string 't ZZZZ' for consistent short time + timezone display.
 * @param dateString - ISO datetime string
 * @param timezone - IANA timezone (e.g., 'America/New_York'). If null/undefined, uses local timezone
 * @returns Formatted time string (e.g., "2:30 PM EST") or empty string if invalid
 * @example
 * formatTimeWithTimezone('2026-01-16T19:30:00Z', 'America/New_York')  // "2:30 PM EST"
 * formatTimeWithTimezone('2026-01-16T19:30:00Z', null)                // "2:30 PM CST" (or user's local tz)
 */
export function formatTimeWithTimezone(
  dateString: string | null | undefined,
  timezone: string | null | undefined
): string {
  if (!dateString) return ''

  // Parse as UTC first (using parseUtcStringToLxDateTime to normalize the string)
  const utcDateTime = parseUtcStringToLxDateTime(dateString)
  if (!utcDateTime) return ''

  const dt = timezone
    ? utcDateTime.setZone(timezone)
    : utcDateTime.setZone('local')

  if (!dt.isValid) return ''

  return dt.toFormat('t ZZZZ')
}
